import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentLanguage } from '../features/langSlice';
import { useGetContentsByTagWebQuery } from '../features/contentsApiSlice';
import CustomLink from './CustomLink';
import PractitionerEn from '../assets/partner/practitioner-en.webp';
import PractitionerDe from '../assets/partner/practitioner-de.webp';
import PartnerRiise from '../assets/partner/Partner-riise.webp';
import {
  HealthierPractitioner,
  HealthierPractitionerP1,
  HealthierPractitionerP2,
  NeedHelpHaveQuestions,
  RegisterHeading,
  RegistrationText,
} from '../Constants';
import LoadingImg from '../assets/partner/loading.png';
import PillImg from '../assets/partner/pill.png';
import { useTranslation } from 'react-i18next';
import { useCreateNutritionistMutation } from '../features/customFormulaApiSlice';
import { toast } from 'react-toastify';
import Input from './elements/Input';
import { COUNTRIES } from '../utils';
import { useNavigate } from 'react-router-dom';
import useMeta from '../hooks/useMeta';
import ReCAPTCHA from 'react-google-recaptcha';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const recaptchaSiteKey = process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY;

const PRACTITIONER_TYPES = [
  'Naturopathic Physician',
  'Chiropractor',
  'Medical Doctor',
  'Nurse Practitioner',
  'Registered Dietitian',
  'Certified Nutritionist',
  'Osteopath',
  'Acupuncturist',
  'Functional Medicine Practitioner',
  'Other',
];

const Practitioner = () => {
  const { t } = useTranslation();
  const { metaEvent } = useMeta();
  const selectedLang = useSelector(selectCurrentLanguage);
  const [lang, setLang] = useState();

  const [data, setData] = useState([]);
  const [skip] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    practitioner_type: '',
    phone_number: '',
    country: '',
    hear_about_us: '',
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [hasRecaptchaLoadError, setHasRecaptchaLoadError] = useState(false);
  const recaptchaRef = useRef(null);

  const [createNutritionist] = useCreateNutritionistMutation();
  const [picture3, setPicture3] = useState('');

  const {
    data: fetchContents,
    isSuccess,
    refetch,
  } = useGetContentsByTagWebQuery({ tag: 'partner', lang: lang, skip: skip });

  useEffect(() => {
    setLang(selectedLang === 'us' ? 'en' : selectedLang);

    refetch();
  }, [refetch, lang, selectedLang]);

  useEffect(() => {
    if (isSuccess) {
      setData(fetchContents.data);

      if (fetchContents.data[0]) {
        setPicture3(`${process.env.REACT_APP_PUBLIC_URL}${fetchContents.data[5].picture}`);
      }
    }
  }, [fetchContents, isSuccess]);

  const handleScroll = () => {
    const el = document.getElementById('registration');
    el?.scrollIntoView({ behavior: 'smooth' });
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim() || form.name.trim().length < 2) {
      nextErrors.name = t('Practitioner_NameError');
    }
    if (!form.email.trim() || !emailRegex.test(form.email.trim())) {
      nextErrors.email = t('Practitioner_EmailError');
    }
    if (!form.practitioner_type.trim()) {
      nextErrors.practitioner_type = t('Practitioner_TypeError');
    }
    if (!form.country.trim()) {
      nextErrors.country = t('Practitioner_CountryError');
    }
    if (!form.hear_about_us.trim()) {
      nextErrors.hear_about_us = t('Practitioner_HearAboutUsError');
    }
    if (!recaptchaSiteKey || hasRecaptchaLoadError) {
      nextErrors.recaptcha = t('Practitioner_CaptchaUnavailable');
    } else if (!recaptchaToken) {
      nextErrors.recaptcha = t('Practitioner_CaptchaError');
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (submitError) setSubmitError('');
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token || '');
    setHasRecaptchaLoadError(false);
    setErrors((prev) => ({ ...prev, recaptcha: '' }));
    setSubmitError('');
  };

  const handleRecaptchaError = () => {
    setRecaptchaToken('');
    setHasRecaptchaLoadError(true);
  };

  const resetRecaptcha = () => {
    setRecaptchaToken('');
    recaptchaRef.current?.reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    setSubmitError('');
    try {
      const res = await createNutritionist({ ...form, recaptchaToken });
      console.log('res => ', res?.data);
      if (res?.data) {
        toast.success(t('Practitioner_CreateSuccess'));
        setSuccessMessage(t('Practitioner_CreateSuccessBody'));
        setForm({
          name: '',
          email: '',
          company: '',
          practitioner_type: '',
          phone_number: '',
          country: '',
          hear_about_us: '',
        });
        resetRecaptcha();
        metaEvent('PractitionerSignup');
      } else {
        const apiError =
          res?.error?.data?.error || res?.error?.data?.message || t('Practitioner_RegisterError');
        setSubmitError(apiError);
        resetRecaptcha();
      }
    } catch {
      setSubmitError(t('Practitioner_NetworkError'));
      resetRecaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  const isSubmitDisabled = isLoading || !recaptchaSiteKey || hasRecaptchaLoadError || !recaptchaToken;

  return (
    <Fragment>
      <section className="t-bg-gray-100 t-pb-4">
        <div className="t-pt-6">
          <div className="container">
            <div className="col-lg-7 mx-auto text-center px-3 t-min-h-[456px] md:t-min-h-[360px]">
              <h4 className="mb-4 top-head">
                {data[0] && <span className="fw-bold" dangerouslySetInnerHTML={{ __html: data[0].title }} />}
              </h4>
              {data[0] && <h5 className="lh-base" dangerouslySetInnerHTML={{ __html: data[0].contents }} />}

              <div className="text-center mt-4">
                <button onClick={handleScroll} className="btn btn-custom-secondary fw-bold lts1 px-3">
                  {t('Practitioner_CreateAccount')}
                </button>
                <div className="t-flex t-items-center t-justify-center t-gap-2 t-mt-2">
                  <p className="!t-m-0">{t('Practitioner_HaveAccount')}</p>
                  <a
                    href="https://riise-partner.weriise.co/practitioner/login"
                    target="_blank"
                    rel="noreferrer"
                    className="t-text-secondary t-underline"
                  >
                    Login
                  </a>
                </div>
              </div>
              {data[2] && (
                <h5 className="lh-base mt-3" dangerouslySetInnerHTML={{ __html: data[2].contents }} />
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="difference-section section-padding">
        <div className="container">
          <div className="t-flex t-flex-col t-gap-8 md:t-gap-16">
            <div className="row">
              <div className="col-lg-6 d-flex flex-column justify-content-center">
                {data[3] && <h4 className="lh-base" dangerouslySetInnerHTML={{ __html: data[3].title }} />}

                {data[3] && <p className="lh-base" dangerouslySetInnerHTML={{ __html: data[3].contents }} />}
              </div>
              <div className="col-lg-6 ">
                <img
                  src={lang === 'de' ? PractitionerDe : PractitionerEn}
                  alt=""
                  className="img-fluid partner-img"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-lg-6 float-start !t-order-2 md:!t-order-1">
                <img src={PartnerRiise} alt="" className="img-fluid partner-img" />
              </div>

              <div className="col-lg-6 d-flex flex-column justify-content-center !t-order-1 md:!t-order-2">
                {data[4] && <h4 className="lh-base" dangerouslySetInnerHTML={{ __html: data[4].title }} />}

                {data[4] && <p className="lh-base" dangerouslySetInnerHTML={{ __html: data[4].contents }} />}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding container t-m-4 t-flex !t-flex-col t-items-center t-justify-center t-gap-12 t-mt-8">
        <h1
          className="t-w-full md:t-w-[75%] t-text-center t-text-2xl md:t-text-3xl !t-m-0 t-font-extrabold"
          style={{ fontFamily: 'Roboto' }}
        >
          {t(HealthierPractitioner)}
        </h1>
        <div className=" t-flex t-flex-col sm:t-flex-row t-items-stretch t-gap-4 sm:t-gap-8 md:t-gap-16 lg:t-gap-32">
          <div className="t-w-auto sm:t-w-[200px] t-flex t-flex-col t-items-center t-justify-center t-gap-4 ">
            <img src={LoadingImg} alt="" className="t-h-14 t-w-14" />
            <p className="t-text-gray-700 t-text-center t-text-base">{t(HealthierPractitionerP1)}</p>
          </div>
          <div className="t-w-auto sm:t-w-[200px] t-flex t-flex-col t-items-center t-justify-center t-gap-4 ">
            <img src={PillImg} alt="" className="t-h-14 t-w-14" />
            <p className="t-text-gray-700 t-text-center t-text-base">{t(HealthierPractitionerP2)}</p>
          </div>
        </div>
      </section>

      <section
        id="registration"
        className="section-padding container t-grid t-grid-cols-5 t-gap-12 t-bg-secondary t-rounded-lg !t-p-4 md:!t-p-8"
      >
        <h1
          className="t-col-span-5 md:t-col-span-3 t-w-full t-text-2xl md:t-text-3xl !t-m-0 t-font-extrabold t-text-white"
          style={{ fontFamily: 'Roboto' }}
        >
          {t(RegistrationText)}
        </h1>
        <div className="t-col-span-5 md:t-col-span-2 t-w-full t-flex-none t-flex t-flex-col t-gap-4 t-bg-white t-p-4 t-rounded-lg">
          <h1 className="t-text-3xl t-text-center" style={{ fontFamily: 'Roboto' }}>
            {t(RegisterHeading)}
          </h1>
          <form className="!t-w-full t-space-y-4" onSubmit={handleSubmit} aria-label={t('Practitioner_RegisterForm')}>
            <Input
              label={t('FirstName')}
              name="name"
              placeholder={t('Practitioner_YourName')}
              value={form.name}
              onChange={onChange}
              error={errors.name}
              className="!t-w-full"
            />
            <Input
              label={t('Email')}
              name="email"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={onChange}
              error={errors.email}
              className="!t-w-full"
            />
            <Input
              label={t('CompanyName')}
              name="company"
              placeholder={t('Practitioner_CompanyName')}
              value={form.company}
              onChange={onChange}
              error={errors.company}
              className="!t-w-full"
            />
            {/* Practitioner Type selector */}
            <div className="t-flex t-flex-col t-gap-2">
              <label className="t-ext-sm t-font-medium t-text-gray-700">{t('Practitioner_Type')}</label>
              <select
                name="practitioner_type"
                value={form.practitioner_type}
                onChange={onChange}
                className="t-w-full t-rounded-md t-border t-border-gray-300 t-px-3 t-py-2 focus:t-outline-none focus:t-ring-2 focus:t-ring-tertiary"
                aria-invalid={!!errors.practitioner_type}
              >
                <option value="">{t('Practitioner_SelectType')}</option>
                {PRACTITIONER_TYPES.map((pt) => (
                  <option key={pt} value={pt}>
                    {pt}
                  </option>
                ))}
              </select>
              {errors.practitioner_type && (
                <span className="text-red-600 text-xs">{errors.practitioner_type}</span>
              )}
            </div>

            {/* Phone number */}
            <Input
              label={t('Practitioner_PhoneNumber')}
              name="phone_number"
              placeholder={t('Practitioner_YourPhoneNumber')}
              value={form.phone_number}
              onChange={onChange}
              error={errors.phone_number}
              className="!t-w-full"
            />

            {/* Country selector */}
            <div className="t-flex t-flex-col t-gap-2">
              <label className="t-text-sm t-font-medium t-text-gray-700">{t('CustomerAddress_Country')}</label>
              <select
                name="country"
                value={form.country}
                onChange={onChange}
                className="t-w-full t-rounded-md t-border t-border-gray-300 t-px-3 t-py-2 focus:t-outline-none focus:t-ring-2 focus:t-ring-tertiary"
                aria-invalid={!!errors.country}
              >
                <option value="">{t('Practitioner_SelectCountry')}</option>
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
              {errors.country && <span className="text-red-600 text-xs">{errors.country}</span>}
            </div>
            <Input
              label={t('Practitioner_HearAboutUs')}
              name="hear_about_us"
              placeholder={t('Practitioner_HearAboutUsPlaceholder')}
              value={form.hear_about_us}
              onChange={onChange}
              error={errors.hear_about_us}
              className="!t-w-full"
            />

            {successMessage && (
              <div
                id="register-success"
                className="t-text-green-600 t-text-sm t-mt-1 t-mb-2"
                role="status"
                aria-live="polite"
              >
                {successMessage}
              </div>
            )}

            {submitError && (
              <div
                id="register-error"
                className="t-text-red-600 t-text-sm t-mt-1 t-mb-2"
                role="alert"
                aria-live="assertive"
              >
                {submitError}
              </div>
            )}

            <div className="t-flex t-flex-col t-gap-2">
              {recaptchaSiteKey && (
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={recaptchaSiteKey}
                  onChange={handleRecaptchaChange}
                  onExpired={resetRecaptcha}
                  onErrored={handleRecaptchaError}
                />
              )}
              {(!recaptchaSiteKey || hasRecaptchaLoadError || errors.recaptcha) && (
                <span className="text-red-600 text-xs">
                  {errors.recaptcha || t('Practitioner_CaptchaUnavailable')}
                </span>
              )}
            </div>

            <div className="t-flex t-items-center t-gap-2">
              <p className="!t-m-0">{t('Practitioner_HaveAccount')}</p>
              <a
                href="https://riise-partner.weriise.co/practitioner/login"
                target="_blank"
                rel="noreferrer"
                className="t-text-secondary t-underline"
              >
                Login
              </a>
            </div>
            <button type="submit" className="t-w-full btn btn-secondary !t-bg-secondary" disabled={isSubmitDisabled}>
              {isLoading ? t('Practitioner_Submitting') : t('Practitioner_Register')}
            </button>
          </form>
        </div>
      </section>

      {/* <section className="testimonials section-padding !t-pb-10 !t-pt-10 t-min-h-[315px] md:t-min-h-[400px]">
        <div className="container">
          <div className="row align-items-center title-wrapper !t-mb-0">
            <div className="col-lg-12 !t-mb-0">
              <div className="t-items-center !t-mb-0">
                <h3 className="t-text-[24px] md:t-text-[30px] !t-text-center t-font-bold t-text-tertiary t-mb-0">
                  {data[2] && (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: homepageContent?.data[2]?.title,
                      }}
                    />
                  )}
                </h3>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <Testimonials />
            </div>
          </div>
        </div>
      </section> */}

      <section
        className="breadcrumbs-wrapper-partner t-min-h-[208px] t-mt-8"
        style={{
          backgroundImage: `linear-gradient(var(--secondary-80),var(--secondary-80)),  url(${picture3})`,
        }}
      >
        <div className="container">
          <div className="col-lg-7 mx-auto text-center px-3">
            <h4 className="mb-4 top-head">{t(NeedHelpHaveQuestions)}</h4>
            {data[5] && <h5 className="lh-base" dangerouslySetInnerHTML={{ __html: data[5].contents }} />}
            <div className="text-center mt-4">
              <CustomLink
                to={'https://app.lemcal.com/@paulantoine/30-minutes'}
                className="btn btn-custom-secondary fw-bold lts1 px-3"
              >
                {data[6] && <span dangerouslySetInnerHTML={{ __html: data[6].title }} />}
              </CustomLink>
            </div>
            {data[7] && (
              <h5 className="lh-base mt-3" dangerouslySetInnerHTML={{ __html: data[7].contents }} />
            )}
          </div>
        </div>
      </section>
    </Fragment>
  );
};

export default Practitioner;
